import { IAngularEvent, IPromise, IScope } from "angular";
import { StateParams, StateService } from "@uirouter/angularjs";
import { HierarchyNode } from "d3-hierarchy";
import { CustomFieldActions } from "@gtmhub/customFields/redux/custom-field-actions";
import { ICustomFieldsStoreState } from "@gtmhub/customFields/redux/custom-field-reducer";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { CloneOrMoveResourceService } from "@gtmhub/okrs/components/clone-okrs/clone-or-move-resource.service";
import { DropdownKeysConfig } from "@gtmhub/shared/directives/dropdown-keys";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { CloneOrMoveResourceService as CloneMoveResourceService } from "@webapp/okrs/components/clone-move-okrs/clone-move-resource.service";
import { ICloningSession, IStratifiedPlanningSession, Session } from "@webapp/sessions/models/sessions.model";
import { PlanningSessionsActions } from "../redux/session-actions";
import { ISessionsStoreState } from "../redux/session-reducer";

const bindStateToScope = (state: ICustomFieldsStoreState & ISessionsStoreState): ICloneSessionsRedux => {
  return {
    customFields: Object.values(state.customFields.map).reduce((customFields, typeCfArr) => ((customFields = [...customFields, ...typeCfArr]), customFields), []),
    customFieldsAreFetched: state.customFields.isFetched,
    allSessions: state.sessions.items,
    sessionNodes: state.sessions.tree ? state.sessions.tree.children : [],
    sessionsAreFetched: state.sessions.isFetched,
    reduxItemsError: state.sessions.error || state.customFields.error,
  };
};

interface ICloneSessionsRedux {
  customFields: ICustomField[];
  customFieldsAreFetched: boolean;
  sessionNodes: HierarchyNode<Session>[];
  allSessions: Session[];
  sessionsAreFetched: boolean;
  reduxItemsError: IUIError;
}
export interface ICloneSessionsScope extends ICloneSessionsRedux, IScope, IExtendedRootScopeService {
  hasCustomFields: boolean;
  hasChildObjectives: boolean;
  sessionTitle: string;
  selectedSession: ICloningSession;
  focusedSession: ICloningSession;
  searchSessionQuery: string;
  sessions: {
    sessionId: string;
    sessionParentId: string;
    title: string;
  }[];
  crossSessionLinkingPresent: boolean;
  isNewCloneAndMoveModalEnabled: boolean;
  originSessionGoals: IGoal[];
  selectSession(session: ICloningSession): void;
  resetSessionProgress(value: boolean): void;
  sendNotifications(): void;
  onSessionSelected(config: DropdownKeysConfig<ICloningSession>): void;
  clearSession(): void;
  cloneTasks(): void;
  cloneCustomFields(): void;
  clone(): void;
  close(): void;
  indicators: {
    loading?: IIndicator;
  };
}

export interface ISelectedCloningSession {
  sessionId: string;
  sessionParentId: string;
  cloneTasks?: boolean;
  cloneCustomFields?: boolean;
  cloneOnlyParentObjectives?: boolean;
  sendNotifications?: boolean;
  resetProgress?: boolean;
  title?: string;
  shouldAlignSession?: boolean;
}

export class CloneSessionsCtrl {
  public static $inject = [
    "$scope",
    "$ngRedux",
    "UIErrorHandlingService",
    "CustomFieldActions",
    "PlanningSessionsActions",
    "GoalService",
    "$state",
    "$stateParams",
    "CloneOrMoveResourceService",
    "FeatureTogglesFacade",
    "CloneMoveResourceService",
  ];

  private planningSession: IStratifiedPlanningSession;
  private allSessions: Session[];

  constructor(
    private $scope: ICloneSessionsScope,
    private $ngRedux: INgRedux,
    private uiErrorHandlingService: UIErrorHandlingService,
    private customFieldActions: CustomFieldActions,
    private planningSessionActions: PlanningSessionsActions,
    private goalService: GoalService,
    private $state: StateService,
    private $stateParams: StateParams,
    private cloneResourceService: CloneOrMoveResourceService,
    private featureToggleService: FeatureTogglesFacade,
    private cloneMoveResourceService: CloneMoveResourceService
  ) {
    this.$scope.$on("sessionCreated", this.onSessionCreated);

    this.$scope.selectedSession = null;

    this.$scope.selectSession = this.selectSession;
    this.$scope.onSessionSelected = this.onSessionSelected;

    this.$scope.clearSession = this.clearSession;
    this.$scope.resetSessionProgress = this.resetSessionProgress;

    this.$scope.cloneTasks = this.cloneTasks;
    this.$scope.cloneCustomFields = this.cloneCustomFields;
    this.$scope.sendNotifications = this.sendNotifications;

    this.$scope.clone = this.clone;
    this.$scope.close = this.close;

    this.$scope.$on("$destroy", this.$ngRedux.connect(bindStateToScope)(this.$scope));
    this.$ngRedux.dispatch(this.planningSessionActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.sessionsAreFetched && this.$scope.customFieldsAreFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => this.init(),
        () => this.uiErrorHandlingService.handleModal(this.$scope.reduxItemsError)
      );
  }

  private init = (): void => {
    this.$scope.indicators = { loading: { progress: true } };
    const { originSessionId } = this.$stateParams;
    const currentSession = this.findSessionInSessionNodes(originSessionId, this.$scope.sessionNodes);

    this.planningSession = currentSession;
    this.allSessions = this.$scope.allSessions;
    this.$scope.sessionTitle = currentSession.data.title;
    this.$scope.sessions = this.mapCloningSessions(currentSession);

    // get cfs from redux for session and show/ hide clone custom field checkbox
    this.$scope.hasCustomFields = this.$scope.customFields.some(
      (cf) => this.planningSession && this.planningSession.id !== null && cf.targetIds && cf.targetIds.includes(this.planningSession.id)
    );

    this.checkA14CloneAndMoveModalsFeatureToggle();

    // Check if session Okrs have child objectives
    this.goalService
      .getGoalsV2({ filter: { sessionId: { $in: [currentSession.id] } }, fields: "parentId,parentGoalSessionId" })
      .then((goals) => {
        delete this.$scope.indicators.loading;
        this.$scope.originSessionGoals = goals.items;
        this.$scope.hasChildObjectives = this.$scope.originSessionGoals.some((goal) => goal.parentId !== "");
      })
      .catch((error: IUIError) => {
        delete this.$scope.indicators.loading;
        this.uiErrorHandlingService.handleModal({
          body: error.message,
        });
      });
  };

  private mapCloningSessions = (planningSession: IStratifiedPlanningSession): ICloningSession[] => {
    return this.allSessions
      .filter((session) => session.id !== planningSession.id && session.status !== "closed")
      .map((session) => {
        return {
          sessionId: session.id,
          sessionParentId: session.parentId,
          title: session.title,
        };
      });
  };

  private selectSession = (session: ICloningSession): void => {
    const hasCrossSessionLinks = this.checkForCrossSessionGoalLinks(this.$scope.originSessionGoals, this.planningSession, session);
    this.$scope.crossSessionLinkingPresent = hasCrossSessionLinks;
    this.$scope.selectedSession = {
      ...session,
      shouldAlignSession: hasCrossSessionLinks,
    };
  };

  private onSessionSelected = ({ focusedElement, keyReleased }: DropdownKeysConfig<ICloningSession>): void => {
    this.$scope.focusedSession = focusedElement;
    if (keyReleased) {
      this.$scope.selectSession(focusedElement);
    }
  };

  private onSessionCreated = (ev: IAngularEvent, session: Session): void => {
    this.$scope.sessions.push({
      sessionId: session.id,
      sessionParentId: session.parentId,
      title: session.title,
    });

    this.$scope.selectSession({
      sessionId: session.id,
      sessionParentId: session.parentId,
      title: session.title,
    });
  };

  private clearSession = (): void => {
    this.$scope.selectedSession = null;
    this.$scope.searchSessionQuery = null;
    this.$scope.focusedSession = null;

    window.setTimeoutOutsideAngular(() => document.getElementById("selectSession").focus());
  };

  private resetSessionProgress = (value: boolean): void => {
    this.$scope.selectedSession.resetProgress = value;
  };

  private cloneTasks = (): void => {
    this.$scope.selectedSession.cloneTasks = !this.$scope.selectedSession.cloneTasks;
  };

  private cloneCustomFields = (): void => {
    this.$scope.selectedSession.cloneCustomFields = !this.$scope.selectedSession.cloneCustomFields;
  };

  private sendNotifications = (): void => {
    this.$scope.selectedSession.sendNotifications = !this.$scope.selectedSession.sendNotifications;
  };

  private clone = () => {
    if (this.$scope.isNewCloneAndMoveModalEnabled) {
      this.cloneMoveResourceService.cloneSession({ source: this.planningSession.id, target: this.$scope.selectedSession });
    } else {
      this.cloneResourceService.cloneSession({ source: this.planningSession.id, target: this.$scope.selectedSession });
    }

    this.close();
  };

  private close = () => {
    this.$state.go("^.^");
  };

  // We traverse all the session nodes array until we find a cloning session with a provided id
  // Planning session with provided id will always be in a node tree
  private findSessionInSessionNodes = (sessionId: string, sessions: HierarchyNode<Session>[]): IStratifiedPlanningSession => {
    let planningSession: IStratifiedPlanningSession;
    let counter = 0;
    while (!planningSession || counter > sessions.length) {
      planningSession = this.findSessionNodeInTree(sessionId, sessions[counter++]);
    }
    return planningSession;
  };

  private findSessionNodeInTree(sessionId: string, sessionNode: HierarchyNode<Session>): IStratifiedPlanningSession {
    if (sessionNode.id === sessionId) {
      return sessionNode as IStratifiedPlanningSession;
    } else if (sessionNode.children != null) {
      let result = null;
      for (let i = 0; result == null && i < sessionNode.children.length; i++) {
        result = this.findSessionNodeInTree(sessionId, sessionNode.children[i]);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }
  private checkForCrossSessionGoalLinks(originSessionGoals: IGoal[], originSession: IStratifiedPlanningSession, session: ICloningSession): boolean {
    // Checks to see if there are cross session links between goals of an origin session and target session
    // cross session linking will be present if goal has a parent which is in a different session and target and origin sessions have different parents
    const isRootSession = originSession.parent.id === "root";
    const originSessionParentId = originSession.parent.id;
    if (isRootSession) {
      return false;
    }
    for (const goal of originSessionGoals) {
      const hasParentGoal = goal.parentId && goal.parentId !== "";
      if (hasParentGoal && goal.parentGoalSessionId !== originSession.id && session.sessionParentId !== originSessionParentId) {
        return true;
      }
    }
    return false;
  }

  private checkA14CloneAndMoveModalsFeatureToggle = (): IPromise<void> => {
    return this.featureToggleService.isFeatureAvailable(FeatureFlag.CloneAndMoveOkrModal).then((isNewCloneAndMoveModalEnabled) => {
      this.$scope.isNewCloneAndMoveModalEnabled = isNewCloneAndMoveModalEnabled;
    });
  };
}
